import {buildErrorCode} from '../../errorCodes/utils/errorCode';
import {TypedEventTarget} from '../../logics/TypedEventTarget';
import {
  ETextParamType_OnReceivedStartScreenShare,
  ETextParamType_OnReceivedStopScreenShare,
  ETextParamType_OnStopScreenShare,
  HEL_AUDIO_TYPE_VOICE
} from '../../logics/const';
import {
  getMixerMuteAt,
  getMixerSettingAt,
  getSkyRoomId,
  getSkywayKey,
  getSkywayName,
  getSkywayOnTellScreenShareStart,
  getSkywayOnTellWaitScreenShare,
  getUseSkywayAudioGainToggle
} from '../../logics/dataOps';
import {getVideo, setVideo} from '../../logics/domOps';
import {ensureScriptLoaded} from '../../logics/externalScriptLoader';
import {hel_chatbadge_ui_update} from '../../logics/gui_caller';
import {hel_net_replace_room_url_with_roomid} from '../../logics/history';
import {getLocalStream, setLocalStream} from '../../logics/localStream';
import {requestSendingReceivedPacketsFromSkyway, hel_setText} from '../../logics/moduleCaller';
import {parseJwt} from '../../logics/parseJwt';
import {hel_isiPhone} from '../../logics/platform';
import {setMicEnabled} from '../../logics/shared';
import {
  videoStreams,
  hel_video_is_playing_same_stream,
  hel_video_stop,
  hel_get_video_stream_use,
  hel_skyway_stop_screen_sharing_via_room
} from '../../logics/video_callee';
import {GetIHeliNetwork} from '../../singleton_registry';
import {
  onNetReceiveVoiceJoin,
  EHeliMicPermissionState,
  onNetPosConnected,
  onNetCreatedPlayerID,
  onNetReceiveVoiceLeave,
  onNetVoiceConnected,
  onNetVoiceDisconnected,
  onNetConnectionError,
  onNetEstablishmentFail,
  onNetOperationError,
  onNetPosDisconnected
} from '../networkCallback';

import type {
  IHeliPositionChannelProvider,
  PositionChannelProviderEventMap
} from './IHeliPositionChannelProvider';
import type {ChannelSession, PositionChannelDetail} from '../../types/network';
import type {IHeliMic, IHeliRtcTokenRoomTarget, Transform} from '../CHeliNetwork';
import type {CHeliPlayer} from '../CHeliPlayer';
import type {CHeliPositionChannel} from '../CHeliPositionChannel';
import type {
  IHeliVoiceChannelProvider,
  VoiceChannelProviderEventMap
} from '../voiceChannelProviders/IHeliVoiceChannelProvider';
import type PeerType from 'skyway-js';
import type {SfuRoom} from 'skyway-js';

declare global {
  // eslint-disable-next-line no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須
  var Peer: typeof PeerType | undefined;
}

const getLocalVideoElement = (): HTMLVideoElement => {
  return document.getElementById('js-local-stream') as HTMLVideoElement;
};

const playLocalStream = async (localStream: MediaStream) => {
  const localVideo = getLocalVideoElement();
  localVideo!.muted = true;
  localVideo!.srcObject = localStream;
  localVideo!.playsInline = true;

  // HTMLVideoElement.playがブラウザレベルの潜在的なバグか何かで失敗時にRejectを返さずに処理がここでストップするような動作をするので暫定的処置としてawaitを削除する
  // https://github.com/videojs/video.js/issues/5441
  localVideo!.play().catch(console.error);
};

const resetLocalStream = () => {
  const localVideo = getLocalVideoElement();
  localVideo.srcObject = null;
  localVideo.load();
};

const setLocalScreenShareStreamBy = async (useAudio: boolean): Promise<MediaStream | null> => {
  const localStream = getLocalStream();
  if (!localStream) {
    return null;
  }
  const oldTrack = localStream.getVideoTracks()[0];
  if (!oldTrack) {
    throw new Error('getLocalStream().getVideoTracks() returns an empty array.');
  }
  const localScreenStream = await window.navigator.mediaDevices
    .getDisplayMedia({
      audio: useAudio,
      video: {
        width: {ideal: 1280},
        height: {ideal: 720},
        frameRate: {ideal: 30}
      }
    })
    .catch(console.error);
  if (!localScreenStream) {
    return null;
  }
  const newTrack = localScreenStream.getVideoTracks()[0];
  if (!newTrack) {
    throw new Error(
      'navigator.mediaDevices.getDisplayMedia().getVideoTracks() returns an empty array.'
    );
  }
  localStream.removeTrack(oldTrack);
  localStream.addTrack(newTrack);
  if (useAudio) {
    const track = localScreenStream.getAudioTracks()[0];
    if (!track) {
      throw new Error(
        'navigator.mediaDevices.getDisplayMedia().getAudioTracks() returns an empty array.'
      );
    }
    localStream.addTrack(track);
  }
  setLocalStream(localStream);
  return localStream;
};

/**
 * @remarks
 * 旧Skywayは廃止されるため、以下の対応を行っていない。
 * - マイクデバイスが存在しない状態から1個以上になったとき、マイク音声を発信できるように。
 */
export class CHeliOldSkywayUnifiedPositionVoiceChannelProvider
  extends TypedEventTarget<PositionChannelProviderEventMap & VoiceChannelProviderEventMap>
  implements IHeliPositionChannelProvider, IHeliVoiceChannelProvider
{
  #room: SfuRoom | null = null;
  #peer: PeerType | null = null;
  readonly #playerIdAudioPairs: Map<
    string,
    {
      readonly element: HTMLAudioElement;
      readonly context: AudioContext | null;
      readonly gainNode: GainNode | null;
      source: MediaStreamAudioSourceNode | null;
      audioDestination: MediaStreamAudioDestinationNode | null;
      attenuateVolume: number;
    }
  > = new Map();
  /**
   * 空のLocalStreamを使用しているかどうか
   * @see https://discord.com/channels/596169759045976094/1243065534916853930/1346337248936857632
   */
  #useLocalEmptyStream: boolean = true;

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    await ensureScriptLoaded('//cdn.webrtc.ecl.ntt.com/skyway-latest.js');
  }

  isConnectedToPositionChannel(positionChannel: CHeliPositionChannel): boolean {
    return true;
  }
  async connect(configKey?: string): Promise<PeerType | boolean> {
    if (!globalThis.Peer) {
      throw new Error('Peer is not defined');
    }

    // LocalStreamの取得
    const localStream = await this.#setLocalAudioStreamBy();

    // マイク許可状態の確認
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    await heliNetwork.checkMicPermissionState();

    // default mic mute
    setMicEnabled(false);

    // Render local stream
    await playLocalStream(localStream);

    const channelSessionJwt = (
      await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
    ).data;
    if (!channelSessionJwt) {
      throw new Error('heliport.v3.api.channelSessionApi.getChannelSessionJwt() returns null.');
    }
    const selfPlayerInfo = parseJwt(channelSessionJwt) as ChannelSession;

    //
    const peer = new globalThis.Peer(selfPlayerInfo.channel_session_code, {
      key: configKey === undefined || configKey === '' ? getSkywayKey() : configKey,
      debug: 0
    });

    this.#peer = peer;

    peer.on('error', (error) => {
      console.error('RTC ERROR', error);
      const errorType = error.type;
      switch (errorType) {
        case 'room-error':
          // ルーム名が指定されていません。
          // そのルーム名は別のタイプのルームで使用されています。他の名前で再度試してください。
          // ルームへの入室中に不明なエラーが発生しました。少し待って、リトライしてください。 etc
          onNetEstablishmentFail(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'RoomError'));
          break;
        case 'permission':
          // 該当のルームの利用が許可されてません。
          onNetEstablishmentFail(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'Permission'));
          break;
        case 'list-error':
          // listAllPeers API が Disabled です。
          onNetOperationError(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'ListError'));
          break;
        case 'disconnected':
          // SkyWay のシグナリングサーバに接続されていません。
          onNetConnectionError(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'Disconnected'));
          break;

        case 'socket-error':
          // SkyWay のシグナリングサーバとの接続が失われました。
          onNetConnectionError(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'SocketError'));
          break;

        case 'invalid-id':
          // peerid が無効です。peerid は 1~63 文字である必要があります。
          onNetEstablishmentFail(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'InvalidId'));
          break;
        case 'unavailable-id':
          // その peerid はすでに使われています。違う peerid を使用してください。
          onNetEstablishmentFail(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'UnavailableId')
          );
          break;
        case 'invalid-key':
          // API キーが無効です。
          onNetEstablishmentFail(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'InvalidId'));
          break;
        case 'invalid-domain':
          // この API キーではそのドメインは登録されていません。
          onNetEstablishmentFail(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'InvalidDomain')
          );
          break;
        case 'authentication':
          // 認証トークンが不正です。
          onNetEstablishmentFail(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'Authentication')
          );
          break;
        case 'server-error':
          // SkyWay のシグナリングサーバとの接続中に問題がありました。 少し待って、リトライしてください。
          onNetConnectionError(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'ServerError'));
          break;
        case 'sfu-client-not-supported':
          // このクライアントは SFU の使用をサポートしていません。最新の Google Chrome を使用してください。
          onNetEstablishmentFail(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'SFUClientNotSupported')
          );
          break;

        case 'peer-unavailable':
          // 対向の peer へ送信できませんでした。peerid が正しいことを確認してください。
          onNetOperationError(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'PeerUnavailable')
          );
          break;

        // 無料枠超過
        case 'signaling-limited':
          onNetEstablishmentFail(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'SignalingLimited')
          );
          break;
        case 'sfu-limited':
          onNetEstablishmentFail(buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'SFULimited'));
          break;
        case 'turn-limited':
          onNetEstablishmentFail(
            buildErrorCode('RTC', 'OldSkyway', 'Peer', 'Event', 'TurnLimited')
          );
          break;
        default:
          onNetEstablishmentFail(buildErrorCode('RTC', 'OldSkyway', 'Other', 'Other', 'Other'));
      }
    });

    return peer;
  }

  async #setLocalAudioStreamBy(): Promise<MediaStream> {
    const audioSettings = await this.#getMicInputOption();
    let localStream: MediaStream;
    try {
      localStream = await window.navigator.mediaDevices.getUserMedia({
        audio: audioSettings,
        video: false
      });

      this.#useLocalEmptyStream = false;
    } catch (error) {
      // マイク無許可時・マイクデバイスが一つも存在しないとき
      console.debug('#setLocalAudioStreamWithDummyVideoBy()', error);

      this.#useLocalEmptyStream = true;

      // 空のStreamを作成する
      localStream = new MediaStream();
    }

    if (hel_get_video_stream_use()) {
      // TODO: 常にtrue？
      const canvas = document.createElement('canvas');
      canvas.getContext('2d');
      const track = canvas.captureStream().getVideoTracks()[0];
      if (!track) {
        throw new Error('canvas.captureStream().getVideoTracks() returns an empty array.');
      }
      localStream.addTrack(track);
    }

    setLocalStream(localStream);
    return localStream;
  }

  /**
   * マイクデバイスを取得
   */
  async #getMicInputOption(): Promise<MediaTrackConstraints> {
    return {deviceId: (await GetIHeliNetwork()?.getMicDevice())?.deviceId ?? undefined};
  }

  async #checkTrackState(): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    // マイクデバイスを取得
    const pIHeliMic = await heliNetwork.getMicDevice();
    await heliNetwork.checkTrackState(pIHeliMic?.deviceId ?? null);
  }

  isConnected(): boolean {
    return this.#peer ? true : false;
  }
  isConnectedPositionChannel(): boolean {
    return true;
  }
  async prepareLeavePositionChannel(): Promise<void> {}
  async postLeaveVoiceChannelCleanup(): Promise<void> {}
  async joinPositionChannel(channelSessionJwt: string): Promise<void> {}

  async leavePositionChannel(): Promise<void> {
    this.#disconnectRoom();
    this.#destroyPeer();
  }
  #destroyPeer() {
    if (!this.#peer) return;

    this.#peer.destroy();
    this.#peer = null;
  }
  #disconnectRoom(): void {
    if (!this.#room) return;

    this.#room.close();
    this.#room = null;
  }

  getPlayerId(): string | null {
    return this.#peer?.id ?? null;
  }
  sendData(param: string): void {
    if (!this.#room) {
      return;
    }

    this.#room.send(param);
  }

  // 他プレーヤのアバター表示切替コールバック
  setPlayerFullAvatarState(player: CHeliPlayer, isFullAvatar: boolean): void {
    // 旧Skyway単版では不使用
  }
  // 他プレーヤの他プレーヤのモーショントラッキング使用状況切替コールバック
  setPlayerMotionTrackingState(player: CHeliPlayer, isMotionTracking: boolean): void {
    // 旧Skyway単版では不使用
  }
  sendHighFrequentSyncSubscribe(player: CHeliPlayer, isFullAvatar: boolean): void {
    throw new Error('Method not implemented.');
  }

  // IHeliVoiceChannelProvider

  #onJoinPositionChannelAbortController: AbortController | null = null;
  #globalPlayerVoiceVolume = getMixerSettingAt(HEL_AUDIO_TYPE_VOICE);

  prepareForNewJoin(): boolean {
    this.#onJoinPositionChannelAbortController?.abort();

    if (!window.navigator.mediaDevices) {
      console.error('navigator.mediaDevices not found');
      return false;
    }

    return true;
  }

  onRemoteJoin(): void {}

  onRemoteLeave(detail: PositionChannelProviderEventMap['remoteleave']['detail']): void {}

  connectVoiceChannel(): void {}

  onData(data: string, playerId: string): void {}

  disconnectFromVoiceChannel(): void {
    resetLocalStream();
  }

  async onJoinPositionChannel(
    positionChannelDetail: PositionChannelDetail,
    channelSessionJwt: string
  ): Promise<void> {
    await this.#checkTrackState();
    if (this.#peer?.open) {
      // 先にpeerがopen状態なので接続を即時実行
      onNetPosConnected();
      this.#connectTalk();
      return;
    }

    // まだopenではないのでコールバックだけ登録しておいてopen後に実行されるようにする
    await new Promise<void>((resolve, reject) => {
      const peer = this.#peer;
      if (!peer) {
        throw new Error('this.#peer is null');
      }

      this.#onJoinPositionChannelAbortController = new AbortController();

      const openEventListener = () => {
        this.#onJoinPositionChannelAbortController?.signal.removeEventListener(
          'abort',
          abortEventListener
        );
        this.#onJoinPositionChannelAbortController = null;

        onNetPosConnected();
        this.#connectTalk();
        resolve();
      };
      peer.once('open', openEventListener);

      const abortEventListener = () => {
        peer.removeListener('open', openEventListener);

        try {
          this.#onJoinPositionChannelAbortController?.signal.throwIfAborted();
        } catch (exception) {
          reject(exception);
        } finally {
          this.#onJoinPositionChannelAbortController = null;
        }
      };
      this.#onJoinPositionChannelAbortController.signal.addEventListener(
        'abort',
        abortEventListener,
        {once: true}
      );
    });
  }
  #connectTalk(): void {
    const roomid = getSkyRoomId();
    // const leaveTrigger = document.getElementById('js-leave-trigger');
    const remoteVideos = document.getElementById('js-remote-streams');
    if (!remoteVideos) {
      throw new Error("document.getElementById('js-remote-streams') returns null.");
    }

    // Note that you need to ensure the peer has connected to signaling server
    // before using methods of peer instance.
    const peer = this.#peer;
    if (!peer?.open) {
      console.error('peer not open');
      return;
    }

    //
    const localStream = getLocalStream();
    if (localStream === undefined) {
      console.error('localStream is not exist');
      return;
    }

    //
    const room = peer.joinRoom(roomid, {
      mode: 'sfu',
      stream: localStream,
      videoReceiveEnabled: true
    });

    this.#room = room;

    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    room.once('open', () => {
      requestSendingReceivedPacketsFromSkyway('open', peer.id, '');

      // to set my name
      requestSendingReceivedPacketsFromSkyway('data', peer.id, 'name:' + getSkywayName());

      // send my name to old player
      heliNetwork.sendMyPlayerInfoToAllPlayer();

      // Carnelianとの互換性でOpenで音声チャンネルのCRPにつながったことにする
      onNetVoiceConnected('');

      // PlayerID(PeerID)が使用可能になったことを通知する
      onNetCreatedPlayerID(peer.id);

      this.dispatchEvent<'afterjoinpositionrtc'>(new Event('afterjoinpositionrtc'));
    });

    room.on('peerJoin', (peerId: string) => {
      // NOTE: new PeerにchannelSessionCodeを指定したため、channelSessionCodeとpeerIdは一致する
      heliNetwork.createPlayer(peerId, null, peerId, null, null);

      // send my name to new player
      heliNetwork.sendMyPlayerInfoToAllPlayer();

      // 新たにジョインしたプレイヤーに対して、既に画面共有していた場合はstreamを送る
      heliNetwork.sendScreenShareStart();

      // 音声チャンネルへのJoinを通知
      onNetReceiveVoiceJoin(peerId, '');
    });

    // Render remote stream for new peer join in the room
    room.on('stream', async (stream) => {
      heliNetwork.setPlayerStream(stream);

      // 映像ストリームを含んでいた場合、再生可能にするため格納する
      const remoteVideoStreamTracks = stream.getVideoTracks();
      if (remoteVideoStreamTracks.length > 0) {
        const remoteVideoStream = new MediaStream(remoteVideoStreamTracks);
        const peerId = stream.peerId || stream.id;
        videoStreams.set(peerId, remoteVideoStream);
      }
    });

    // 画面共有用にstreamを受けるのに使用する
    peer.on('call', (mediaConnection) => {
      console.debug('Skyway called');

      // このanswerで答えることでこの下のstreamを受けることが出来る
      mediaConnection.answer(getLocalStream());

      mediaConnection.on('stream', async (stream) => {
        console.debug('Skyway call stream');

        // Render remote stream for callee
        const updatedVideo = document.getElementById('video0') as HTMLVideoElement;
        setVideo(updatedVideo);

        const video = getVideo();
        if (video) {
          console.debug('video connect');

          video.srcObject = stream;
          video.playsInline = true;
          await video.play().catch(console.error);
        } else {
          console.error('video null');
        }
      });
    });

    //
    room.on('data', ({data, src}: {data: string; src: string}) => {
      // Show a message sent to the room and who sent
      //      messages.textContent += `${src}: ${data}\n`;

      if (data === 'screenshare:tell') {
        const fn = getSkywayOnTellScreenShareStart();
        if (fn !== undefined) {
          fn();
          return;
        }
      } else if (data === 'screenshare:waitstart') {
        const fn = getSkywayOnTellWaitScreenShare();
        if (fn !== undefined) {
          fn();
          return;
        }
      }
      if (data.startsWith('screenshare:')) {
        const paramsScreenShare = data.split(':');
        if (paramsScreenShare[1] === 'start') {
          if (paramsScreenShare[2] === undefined || paramsScreenShare[3] === undefined) {
            throw new Error(
              `Invalid screenshare:start format. Expected “screenshare:start:<videoItemName>:<videoItemMaterial>”, but got “${data}”.`
            );
          }

          const remoteStream = videoStreams.get(src);
          if (hel_video_is_playing_same_stream(remoteStream)) {
            return;
          }

          // グローバルにhel_onReceiveNoticeScreenShareStartが定義されていた場合に実行する
          if (typeof globalThis.hel_onReceiveNoticeScreenShareStart === 'function') {
            globalThis.hel_onReceiveNoticeScreenShareStart(
              src,
              remoteStream,
              paramsScreenShare[2],
              paramsScreenShare[3]
            );
          }

          // 予約
          heliNetwork.reserveRemotoVideoStream(
            remoteStream,
            paramsScreenShare[2],
            paramsScreenShare[3]
          );

          // プレイヤー情報に画面共有中のフラグを設定する
          const player = heliNetwork.getPlayer(src);
          if (player != null) {
            player.screenSharing = true;
          }

          // HeliScriptのコールバックを呼び出す
          hel_setText(ETextParamType_OnReceivedStartScreenShare, '');
        }
        if (paramsScreenShare[1] === 'stop') {
          this.#stopCallScreenShare(src);

          // プレイヤー情報の画面共有中フラグを解除する
          const player = heliNetwork.getPlayer(src);
          if (player != null) {
            player.screenSharing = false;
          }
        }
      }
      requestSendingReceivedPacketsFromSkyway('data', src, data);

      if (data.startsWith('join:')) {
        (async () => {
          while (!heliNetwork.getPlayer(src)) {
            // NOTE: 先に入室していたプレイヤーについて、peerJoinイベントが受信できない場合がある。
            // その場合はstreamイベントの受信を待つことになるが、これはdataイベントのjoin受信よりも遅いタイミングとなるため、ここで待機する。
            await new Promise((resolve) => {
              window.setTimeout(resolve, 50);
            });
          }
          this.dispatchEvent<'remotejoin'>(
            new CustomEvent('remotejoin', {detail: {playerId: src}})
          );
        })();
      } else if (data.startsWith('msg:')) {
        // dataが 'msg:' の場合は、チャットメッセージ。なのでチャットのバッジ更新を行う。
        hel_chatbadge_ui_update(); // チャットログバッジのUI更新
      }
    });

    // for closing room members
    room.on('peerLeave', (peerId: string) => {
      // そのプレイヤーが画面共有中であれば解除する
      const player = heliNetwork.getPlayer(peerId);
      if (player != null && player.screenSharing) {
        this.#stopCallScreenShare(peerId);
      }

      const remoteVideo = remoteVideos.querySelector(
        `[data-peer-id="${peerId}"]`
      ) as HTMLVideoElement;

      if (remoteVideo && remoteVideo.srcObject) {
        if (!('getTracks' in remoteVideo.srcObject)) {
          throw new Error('remoteVideo.srcObject is not an instance of MediaStream.');
        }
        remoteVideo.srcObject.getTracks().forEach((track) => track.stop());
        remoteVideo.srcObject = null;
        remoteVideo.remove();
      }

      if (videoStreams.has(peerId)) {
        // ビデオストリームの除去
        videoStreams.delete(peerId);
        if (typeof globalThis.hel_onReceiveNoticeScreenShareStop === 'function') {
          globalThis.hel_onReceiveNoticeScreenShareStop(peerId);
        }
      }

      // messages.textContent += `=== ${peerId} left ===\n`;
      // hel_skyway_add_text_chat("", `${peerId} leaved`);

      this.dispatchEvent<'remoteleave'>(
        new CustomEvent('remoteleave', {detail: {playerId: peerId}})
      );

      // 音声チャンネルからのLeaveを通知
      onNetReceiveVoiceLeave(peerId, '');
    });

    // for closing myself
    room.once('close', () => {
      Array.from(remoteVideos.getElementsByTagName('video')).forEach((remoteVideo) => {
        if (!remoteVideo.srcObject) {
          throw new Error('remoteVideo.srcObject is null.');
        }
        if (!('getTracks' in remoteVideo.srcObject)) {
          throw new Error('remoteVideo.srcObject is not an instance of MediaStream.');
        }
        remoteVideo.srcObject.getTracks().forEach((track) => track.stop());
        remoteVideo.srcObject = null;
        remoteVideo.remove();
      });

      heliNetwork.releaseAllPlayer();

      // Carnelianとの互換性でcloseで音声チャンネルのCRPから切断したことにする
      onNetVoiceDisconnected();

      this.dispatchEvent<'afterjoinpositionrtc'>(new Event('afterjoinpositionrtc'));

      // 座標チャンネルからの退出を通知
      onNetPosDisconnected();
    });

    // roomidをURLに反映する
    hel_net_replace_room_url_with_roomid(roomid);
  }
  #stopCallScreenShare(peerId: string): void {
    // グローバルにhel_onReceiveNoticeScreenShareStopが定義されていた場合に実行する
    if (typeof globalThis.hel_onReceiveNoticeScreenShareStop === 'function') {
      globalThis.hel_onReceiveNoticeScreenShareStop(peerId);
    } else {
      hel_video_stop();

      // HeliScriptのコールバックを呼び出す
      hel_setText(ETextParamType_OnReceivedStopScreenShare, '');
    }
  }

  initializePlayerVoice(player: CHeliPlayer): void {
    const playerId = player.getPlayerId();

    const element = document.createElement('audio');
    element.setAttribute('data-peer-id', playerId);
    let context: AudioContext | null = null;
    let gainNode: GainNode | null = null;
    if (getUseSkywayAudioGainToggle() && hel_isiPhone()) {
      context = new (window.AudioContext ?? window.webkitAudioContext)();
      gainNode = context.createGain();
    }
    this.#playerIdAudioPairs.set(playerId, {
      element,
      context,
      gainNode,
      source: null,
      audioDestination: null,
      attenuateVolume: 0.0
    });

    this.#applyPlayerVolume(player);
  }
  #getPlayerAudio(player: CHeliPlayer) {
    const playerId = player.getPlayerId();
    const audio = this.#playerIdAudioPairs.get(playerId);
    if (!audio) {
      throw new Error(
        `initializePlayerVoice() must be called for playerId “${playerId}” before calling #getPlayerAudio().`
      );
    }
    return audio;
  }
  onPlayerRemoved(player: CHeliPlayer): void {}
  setPlayerVoiceStream(playerOrPlayerId: CHeliPlayer | string, stream: MediaStream): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    if (typeof playerOrPlayerId === 'string') {
      // NOTE: new PeerにchannelSessionCodeを指定したため、channelSessionCodeとpeerIdは一致する
      heliNetwork.createPlayer(playerOrPlayerId, null, playerOrPlayerId, null, null);

      const player = heliNetwork.getPlayer(playerOrPlayerId);
      if (!player) {
        console.error('CreatePlayer error : ' + playerOrPlayerId);
        return;
      }

      // 音声チャンネルへのJoinを通知
      onNetReceiveVoiceJoin(playerOrPlayerId, '');

      playerOrPlayerId = player;
    }
    // マイクを許可している場合のみstreamを再生
    if (heliNetwork.getMicPermissionState() === EHeliMicPermissionState.Granted) {
      if (getUseSkywayAudioGainToggle()) {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          const audio = this.#getPlayerAudio(playerOrPlayerId);
          if (hel_isiPhone()) {
            if (audio.context) {
              audio.source = audio.context.createMediaStreamSource(new MediaStream([audioTrack]));
              audio.audioDestination = audio.context.createMediaStreamDestination();
            }
            if (audio.gainNode && audio.source && audio.audioDestination) {
              audio.source.connect(audio.gainNode);
              audio.gainNode.connect(audio.audioDestination);
            }
          }
          stream.removeTrack(audioTrack);
          if (audio.audioDestination) {
            const track = audio.audioDestination.stream.getAudioTracks()[0];
            if (!track) {
              throw new Error(
                'this.#getPlayerAudio().audioDestination.stream.getAudioTracks() returns an empty array.'
              );
            }
            stream.addTrack(track);
          }

          if (hel_isiPhone()) {
            if (audio.audioDestination) {
              audio.element.srcObject = audio.audioDestination.stream;
            }
          } else {
            audio.element.srcObject = new MediaStream([audioTrack]);
          }
          audio.element.play().catch(console.error);
        }
      }

      this.#applyPlayerVolume(playerOrPlayerId);
    }
  }
  #applyPlayerVolume(player: CHeliPlayer) {
    if (!getUseSkywayAudioGainToggle()) {
      return;
    }
    const audio = this.#getPlayerAudio(player);
    // player個人のミュートもしくは自身の設定からミュートの場合はvolumeを0にする
    const isMuted = getMixerMuteAt(HEL_AUDIO_TYPE_VOICE);
    const mute = player.mute || isMuted ? true : false;
    const fixedVolume = this.#globalPlayerVoiceVolume * audio.attenuateVolume * (mute ? 0.0 : 1.0);
    if (hel_isiPhone()) {
      if (audio.gainNode) {
        audio.gainNode.gain.value = fixedVolume;
      }
    } else {
      audio.element.volume = fixedVolume;
    }
  }
  applyPlayerVoiceMute(player: CHeliPlayer): void {
    this.#applyPlayerVolume(player);
  }
  setPlayerVoiceAttenuate(player: CHeliPlayer, volume: number): void {
    this.#getPlayerAudio(player).attenuateVolume = volume;
    this.#applyPlayerVolume(player);
  }
  setGlobalPlayerVoiceVolume(volume: number): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    this.#globalPlayerVoiceVolume = volume;
    for (const player of heliNetwork.getPlayerList().values()) {
      this.#applyPlayerVolume(player);
    }
  }

  async startScreenShareStream(useCamera: boolean, useAudio: boolean): Promise<MediaStream | null> {
    let localVideoMediaStream: MediaStream | null;

    if (useCamera) {
      localVideoMediaStream =
        (await window.navigator.mediaDevices
          .getUserMedia({
            audio: useAudio,
            video: true
          })
          .catch(console.error)) ?? null;
    } else {
      localVideoMediaStream = await setLocalScreenShareStreamBy(useAudio);
    }
    if (!localVideoMediaStream) {
      return null;
    }

    // Webブラウザから画面共有停止したときのイベントを設定する
    const track = localVideoMediaStream.getVideoTracks()[0];
    if (!track) {
      throw new Error('localVideoMediaStream.getVideoTracks() returns an empty array.');
    }
    track.addEventListener('ended', () => {
      hel_skyway_stop_screen_sharing_via_room();
    });

    this.#replaceLocalStream(localVideoMediaStream);

    return localVideoMediaStream;
  }
  #replaceLocalStream(stream: MediaStream): void {
    if (this.#room == null) {
      console.error('replaceLocalStream : m_room is null');
      return;
    }

    this.#room.replaceStream(stream);
    setLocalStream(stream);
  }
  async cleanupScreenShareStream(): Promise<boolean> {
    const localStream = getLocalStream();
    if (!localStream) {
      return false;
    }

    localStream.getVideoTracks().forEach((track) => track.stop());

    const track = localStream.getVideoTracks()[0];
    if (!track) {
      throw new Error('getLocalStream().getVideoTracks() returns an empty array.');
    }
    localStream.removeTrack(track);
    const canvas = document.createElement('canvas');
    canvas.getContext('2d');
    const dummyTrack = canvas.captureStream().getVideoTracks()[0];
    if (!dummyTrack) {
      throw new Error('canvas.captureStream().getVideoTracks() returns an empty array.');
    }
    localStream.addTrack(dummyTrack);
    setLocalStream(localStream);
    this.#replaceLocalStream(localStream);
    this.sendData('screenshare:stop');

    // HeliScriptのコールバックを呼び出す
    hel_setText(ETextParamType_OnStopScreenShare, '');

    return true;
  }
  onPlayScreenShare(): void {
    if (!this.#room) return;

    this.#room.on('stream', (stream) => {
      const video = getVideo();

      if (video !== undefined) {
        if (video.srcObject !== stream) {
          const videoTracks = stream.getVideoTracks();
          if (videoTracks.length > 0) {
            video.srcObject = stream;
            video.playsInline = true;
          }
        }
      }
    });
  }
  /**
   * @inheritdoc
   * @remarks
   * LocalEmptyStreamを使用しているかで判別する(旧Skywayではマイク不許可時でもマルチプレイを行うために必ず何かしらのLocalStreamが生成されるようになっている)
   * @see https://discord.com/channels/596169759045976094/1243065534916853930/1346337248936857632
   */
  useNotEmptyLocalStream(): boolean {
    return !this.#useLocalEmptyStream;
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    const localStream = getLocalStream();
    if (!localStream) {
      return;
    }
    const track = localStream.getAudioTracks()[0];
    if (!track) {
      return;
    }
    track.enabled = enabled;
  }
  getMicEnabled(): boolean {
    const localStream = getLocalStream();
    if (!localStream) {
      return false;
    }
    const track = localStream.getAudioTracks()[0];
    if (!track) {
      return false;
    }
    return track.enabled;
  }
  supportsSpeakerMode(): boolean {
    return false;
  }

  setCurrentLocalTransform(param: {player: Transform; camera: Transform}): void {}

  async joinVoiceRtc(
    pIHeliMic: IHeliMic | null,
    pIHeliRtcTokenRoomTarget: IHeliRtcTokenRoomTarget | null
  ): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async initLocalMicInput(deviceId: string | null): Promise<boolean> {
    return false;
  }
  async changeMicDevice(deviceId: string): Promise<boolean> {
    if (!this.#room) {
      return false;
    }
    const localStream = getLocalStream();
    if (localStream === undefined) {
      console.error('localStream is not exist');
      return false;
    }
    const currentAudioTrack = localStream.getAudioTracks()[0];
    let audioEnabled = false;
    if (currentAudioTrack) {
      audioEnabled = currentAudioTrack.enabled;
      localStream.removeTrack(currentAudioTrack);
    }
    const micStream = await window.navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: {exact: deviceId}
      },
      video: false
    });
    const newAudioTrack = micStream.getAudioTracks()[0];
    if (!newAudioTrack) throw new Error();
    newAudioTrack.enabled = audioEnabled;
    localStream.addTrack(newAudioTrack);
    this.#replaceLocalStream(localStream);
    return true;
  }

  setSpeakerMode(speakerMode: boolean) {
    return false;
  }

  connectRoomAfterPeerOpen(): void {
    if (!this.#peer) {
      throw new Error('peer has not been set yet');
    }

    this.#peer.on('open', () => {
      // 既にコールバックが登録されているのなら実行しない
      if (this.#onJoinPositionChannelAbortController) return;

      this.#connectTalk();
    });
  }
}
